// ============================================================
// src/components/Profile.jsx
// Perfil del usuario: stats, progreso por materia y badges
// ============================================================

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ref, get } from 'firebase/database';
import { database } from '../services/firebase';
import { RadarChart, Radar, PolarGrid, PolarAngleAxis, ResponsiveContainer, Tooltip } from 'recharts';

import fisicaData from '../data/fisica_openmind.json';
import quimicaData from '../data/quimica_openmind.json';
import biologiaData from '../data/biologia_openmind.json';
import historiaData from '../data/historia_openmind.json';
import politicaData from '../data/politica_openmind.json';
import arteData from '../data/arte_openmind.json';

const ALL_SUBJECTS = [
  { ...fisicaData, color: 'from-blue-500 to-cyan-500', bg: 'bg-blue-50', accent: 'text-blue-600', bar: 'bg-blue-500' },
  { ...quimicaData, color: 'from-emerald-500 to-green-500', bg: 'bg-emerald-50', accent: 'text-emerald-600', bar: 'bg-emerald-500' },
  { ...biologiaData, color: 'from-purple-500 to-violet-500', bg: 'bg-purple-50', accent: 'text-purple-600', bar: 'bg-purple-500' },
  { ...historiaData, color: 'from-amber-500 to-orange-500', bg: 'bg-amber-50', accent: 'text-amber-600', bar: 'bg-amber-500' },
  { ...politicaData, color: 'from-red-500 to-rose-500', bg: 'bg-red-50', accent: 'text-red-600', bar: 'bg-red-500' },
  { ...arteData, color: 'from-pink-500 to-fuchsia-500', bg: 'bg-pink-50', accent: 'text-pink-600', bar: 'bg-pink-500' },
];

// Niveles del sistema de XP
const LEVELS = [
  { level: 1, name: 'Explorador', min: 0, max: 199, icon: '🌱' },
  { level: 2, name: 'Aprendiz', min: 200, max: 499, icon: '📚' },
  { level: 3, name: 'Pensador', min: 500, max: 999, icon: '💡' },
  { level: 4, name: 'Analista', min: 1000, max: 1799, icon: '🔍' },
  { level: 5, name: 'Experto', min: 1800, max: 2999, icon: '🎯' },
  { level: 6, name: 'Maestro', min: 3000, max: 4799, icon: '🏆' },
  { level: 7, name: 'Sabio', min: 4800, max: Infinity, icon: '🌟' },
];

export default function Profile({ user }) {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalPoints: 0,
    level: 1,
    streak: 0,
    completedTopics: 0,
    lastActive: null,
  });
  const [progress, setProgress] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    loadData();
  }, [user]);

  const loadData = async () => {
    try {
      const [statsSnap, progressSnap] = await Promise.all([
        get(ref(database, `users/${user.uid}/stats`)),
        get(ref(database, `users/${user.uid}/progress`)),
      ]);
      if (statsSnap.exists()) setStats(statsSnap.val());
      if (progressSnap.exists()) setProgress(progressSnap.val());
    } catch (err) {
      console.error('Error cargando perfil:', err);
    } finally {
      setLoading(false);
    }
  };

  // Info del nivel actual
  const levelInfo = LEVELS.find((l) => l.level === (stats.level || 1)) || LEVELS[0];
  const nextLevel = LEVELS.find((l) => l.level === (stats.level || 1) + 1);
  const pointsInLevel = (stats.totalPoints || 0) - levelInfo.min;
  const pointsNeeded = nextLevel ? nextLevel.min - levelInfo.min : 1;
  const levelPct = Math.min(100, Math.round((pointsInLevel / pointsNeeded) * 100));

  // Progreso por materia
  const getSubjectStats = (subject) => {
    const subProg = progress[subject.subject] || {};
    const allTopics = subject.levels.flatMap((l) => l.topics);
    const completed = allTopics.filter((t) => subProg[t.topicId]?.completed).length;
    const points = allTopics.reduce((acc, t) => acc + (subProg[t.topicId]?.score || 0), 0);
    return {
      completed,
      total: allTopics.length,
      pct: Math.round((completed / allTopics.length) * 100),
      points,
    };
  };

  // Datos para el gráfico radar
  const radarData = ALL_SUBJECTS.map((s) => {
    const st = getSubjectStats(s);
    return { subject: s.displayName, value: st.pct, fullMark: 100 };
  });

  // Todos los badges desbloqueados
  const allBadges = ALL_SUBJECTS.flatMap((subject) => {
    const subProg = progress[subject.subject] || {};
    return subject.levels
      .flatMap((l) => l.topics)
      .filter((t) => subProg[t.topicId]?.completed)
      .flatMap((t) => t.badges || []);
  });

  const firstName = user?.displayName?.split(' ')[0] || user?.email?.split('@')[0];
  const initial = (user?.displayName || user?.email || 'U')[0].toUpperCase();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="text-5xl animate-bounce mb-3">👤</div>
          <p className="text-gray-400">Cargando perfil...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-12">
      {/* ── Header del perfil ─────────────────────────────── */}
      <div className="bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500 px-4 pt-8 pb-16">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={() => navigate('/')}
            className="text-white/70 hover:text-white text-sm mb-4 flex items-center gap-1"
          >
            ← Volver al inicio
          </button>
          <div className="flex items-center gap-5">
            {/* Avatar */}
            <div className="w-20 h-20 bg-white/20 rounded-3xl flex items-center justify-center text-4xl font-extrabold text-white shadow-lg">
              {initial}
            </div>
            <div className="text-white">
              <h1 className="text-2xl md:text-3xl font-extrabold">{user?.displayName || firstName}</h1>
              <p className="text-indigo-200 text-sm">{user?.email}</p>
              <div className="flex items-center gap-2 mt-2">
                <span className="text-2xl">{levelInfo.icon}</span>
                <span className="font-bold">{levelInfo.name}</span>
                <span className="text-indigo-200 text-sm">· Nivel {stats.level}</span>
              </div>
            </div>
          </div>

          {/* Barra de nivel */}
          <div className="mt-5 bg-white/10 rounded-2xl p-4">
            <div className="flex justify-between text-white text-xs mb-2">
              <span>{levelInfo.icon} {levelInfo.name}</span>
              {nextLevel && (
                <span>{nextLevel.icon} {nextLevel.name} en {nextLevel.min - (stats.totalPoints || 0)} pts</span>
              )}
            </div>
            <div className="bg-white/20 rounded-full h-3">
              <div
                className="bg-white h-3 rounded-full transition-all duration-700"
                style={{ width: `${levelPct}%` }}
              />
            </div>
            <p className="text-white/70 text-xs mt-1 text-center">
              {stats.totalPoints || 0} puntos totales
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 -mt-8 space-y-6">
        {/* ── Stats rápidas ────────────────────────────────── */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { icon: '⭐', value: stats.totalPoints || 0, label: 'Puntos totales' },
            { icon: '✅', value: stats.completedTopics || 0, label: 'Temas completados' },
            { icon: '🔥', value: stats.streak || 0, label: 'Días de racha' },
            { icon: '🏅', value: allBadges.length, label: 'Badges obtenidos' },
          ].map(({ icon, value, label }) => (
            <div key={label} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 text-center">
              <div className="text-3xl mb-1">{icon}</div>
              <div className="text-2xl font-extrabold text-gray-800">{value}</div>
              <div className="text-xs text-gray-500">{label}</div>
            </div>
          ))}
        </div>

        {/* ── Gráfico radar de habilidades ─────────────────── */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <h2 className="font-bold text-gray-800 mb-4">📊 Mapa de conocimiento</h2>
          {stats.completedTopics > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <RadarChart data={radarData}>
                <PolarGrid stroke="#e5e7eb" />
                <PolarAngleAxis dataKey="subject" tick={{ fill: '#6b7280', fontSize: 11 }} />
                <Radar
                  name="Progreso"
                  dataKey="value"
                  stroke="#6366f1"
                  fill="#6366f1"
                  fillOpacity={0.2}
                  strokeWidth={2}
                />
                <Tooltip formatter={(v) => [`${v}%`, 'Completado']} />
              </RadarChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center py-10 text-gray-400">
              <p className="text-4xl mb-2">🗺️</p>
              <p>Completa temas para ver tu mapa de conocimiento</p>
            </div>
          )}
        </div>

        {/* ── Progreso por materia ─────────────────────────── */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <h2 className="font-bold text-gray-800 mb-4">📚 Progreso por materia</h2>
          <div className="space-y-4">
            {ALL_SUBJECTS.map((subject) => {
              const st = getSubjectStats(subject);
              return (
                <button
                  key={subject.subject}
                  onClick={() => navigate(`/subject/${subject.subject}`)}
                  className="w-full text-left hover:bg-gray-50 rounded-xl p-2 transition group"
                >
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xl">{subject.icon}</span>
                      <span className="font-semibold text-gray-700 group-hover:text-indigo-600 transition">
                        {subject.displayName}
                      </span>
                    </div>
                    <div className="text-right">
                      <span className="text-sm font-bold text-gray-700">
                        {st.completed}/{st.total}
                      </span>
                      <span className="text-xs text-gray-400 ml-1">temas</span>
                    </div>
                  </div>
                  <div className="bg-gray-100 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full ${subject.bar} transition-all duration-700`}
                      style={{ width: `${st.pct}%` }}
                    />
                  </div>
                  <div className="flex justify-between mt-1">
                    <span className={`text-xs ${subject.accent} font-medium`}>{st.pct}%</span>
                    <span className="text-xs text-gray-400">{st.points} pts</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Badges ───────────────────────────────────────── */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <h2 className="font-bold text-gray-800 mb-4">
            🏅 Badges obtenidos ({allBadges.length})
          </h2>
          {allBadges.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {allBadges.map((badge, i) => (
                <span
                  key={`${badge}-${i}`}
                  className="bg-amber-50 border border-amber-200 text-amber-700 text-sm font-medium px-3 py-1.5 rounded-full"
                >
                  🏅 {badge}
                </span>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-400">
              <p className="text-4xl mb-2">🔒</p>
              <p>Completa temas para desbloquear badges</p>
            </div>
          )}
        </div>

        {/* ── Logros del sistema de niveles ────────────────── */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <h2 className="font-bold text-gray-800 mb-4">🎯 Sistema de niveles</h2>
          <div className="space-y-2">
            {LEVELS.map((lvl) => {
              const reached = (stats.level || 1) >= lvl.level;
              const current = (stats.level || 1) === lvl.level;
              return (
                <div
                  key={lvl.level}
                  className={`flex items-center gap-3 p-3 rounded-xl border transition
                    ${current
                      ? 'bg-indigo-50 border-indigo-200'
                      : reached
                      ? 'bg-green-50 border-green-100'
                      : 'bg-gray-50 border-gray-100 opacity-50'}`}
                >
                  <span className="text-2xl">{lvl.icon}</span>
                  <div className="flex-1">
                    <span className={`font-semibold text-sm ${current ? 'text-indigo-700' : reached ? 'text-green-700' : 'text-gray-400'}`}>
                      Nivel {lvl.level}: {lvl.name}
                    </span>
                    <p className="text-xs text-gray-400">
                      {lvl.min === 0 ? '0' : lvl.min.toLocaleString()} – {lvl.max === Infinity ? '∞' : lvl.max.toLocaleString()} puntos
                    </p>
                  </div>
                  {reached && <span className="text-green-500 text-lg">{current ? '👈' : '✅'}</span>}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
